import React, { useEffect, useState } from 'react';
import { Plus, Minus } from 'lucide-react';
import {
  createRestaurantRevenueSource,
  updateRestaurantRevenueSource,
  FrontendRestaurantRevenueSource,
} from '../../services/restaurant/restaurantRevenueSourceService';
import { RevenueSource, getRevenueSourceDisplayName, getAvailableRevenueSources } from '../../types/RevenueSource';
import { RevenueSourceType, getRevenueSourceTypeDisplayName, getAvailableRevenueSourceTypes } from '../../types/RevenueSourceType';
import { useTranslation } from 'react-i18next';

export type Mode = 'create' | 'edit';

interface SaveRevenueSourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: Mode;
  restaurantId: string;
  initial?: FrontendRestaurantRevenueSource;
  onSaved: () => void;
}

export const SaveRevenueSourceModal: React.FC<SaveRevenueSourceModalProps> = ({
  isOpen,
  onClose,
  mode,
  restaurantId,
  initial,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [revenueSourceName, setRevenueSourceName] = useState<RevenueSource | ''>(
    (initial?.revenueSourceName as RevenueSource) || ''
  );
  const [revenueSourceType, setRevenueSourceType] = useState<RevenueSourceType | ''>(
    initial?.revenueSourceType || ''
  );
  const [restaurantName, setRestaurantName] = useState(initial?.restaurantName || '');
  const [relevantSections, setRelevantSections] = useState<string[]>(initial?.relevantSections || []);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setRevenueSourceName((initial?.revenueSourceName as RevenueSource) || '');
      setRevenueSourceType(initial?.revenueSourceType || '');
      setRestaurantName(initial?.restaurantName || '');
      setRelevantSections(initial?.relevantSections || []);
      setError(null);
      setSaving(false);
    }
  }, [isOpen, initial]);

  const handleSave = async () => {
    if (!revenueSourceName || !revenueSourceType || !restaurantName) {
      setError(t('restaurantRevenueSources.errorRequired'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (mode === 'create') {
        await createRestaurantRevenueSource({
          restaurantId,
          revenueSourceType: revenueSourceType as RevenueSourceType,
          revenueSourceName: revenueSourceName,
          restaurantName: restaurantName.trim(),
          relevantSections: relevantSections.filter(s => s.trim()),
        });
      } else if (initial) {
        await updateRestaurantRevenueSource(initial.id, {
          revenueSourceType: revenueSourceType as RevenueSourceType,
          revenueSourceName: revenueSourceName,
          restaurantName: restaurantName.trim(),
          relevantSections: relevantSections.filter(s => s.trim()),
        });
      }
      onSaved();
      onClose();
    } catch (err: any) {
      console.error(err);
      // Try to extract error message from the response
      let errorMessage = t('restaurantRevenueSources.errorSave');
      if (err?.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const addSection = () => {
    setRelevantSections([...relevantSections, '']);
  };

  const removeSection = (index: number) => {
    setRelevantSections(relevantSections.filter((_, i) => i !== index));
  };

  const updateSection = (index: number, value: string) => {
    const updated = [...relevantSections];
    updated[index] = value;
    setRelevantSections(updated);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/50" onClick={onClose}>
      <div
        className="bg-card dark:bg-card-dark rounded-3xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] border border-zinc-200 dark:border-white/20"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 md:p-8">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-zinc-900 dark:text-zinc-50">
              {mode === 'create' ? t('restaurantRevenueSources.addRevenueSource') : t('restaurantRevenueSources.editRevenueSource')}
            </h2>
            <button
              onClick={onClose}
              className="flex-none p-2 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
              aria-label={t('restaurantRevenueSources.close')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
              </svg>
            </button>
          </div>

          {/* Form */}
          <div className="space-y-5">
            {/* Revenue Source Name */}
            <div>
              <label htmlFor="revenueSourceName" className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
                {t('restaurantRevenueSources.revenueSourceName')}
              </label>
              <select
                id="revenueSourceName"
                className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:focus:ring-emerald-500/20 focus:border-emerald-500 transition"
                value={revenueSourceName}
                onChange={e => setRevenueSourceName(e.target.value as RevenueSource)}
              >
                <option value="" disabled>{t('restaurantRevenueSources.selectRevenueSource')}</option>
                {getAvailableRevenueSources().map(source => (
                  <option key={source} value={source}>
                    {getRevenueSourceDisplayName(source)}
                  </option>
                ))}
              </select>
            </div>

            {/* Revenue Source Type */}
            <div>
              <label htmlFor="revenueSourceType" className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
                {t('restaurantRevenueSources.revenueSourceType')}
              </label>
              <select
                id="revenueSourceType"
                className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:focus:ring-emerald-500/20 focus:border-emerald-500 transition"
                value={revenueSourceType}
                onChange={e => setRevenueSourceType(e.target.value as RevenueSourceType)}
              >
                <option value="" disabled>{t('restaurantRevenueSources.selectRevenueSourceType')}</option>
                {getAvailableRevenueSourceTypes().map(type => (
                  <option key={type} value={type}>
                    {getRevenueSourceTypeDisplayName(type)}
                  </option>
                ))}
              </select>
            </div>

            {/* Restaurant Name */}
            <div>
              <label htmlFor="restaurantName" className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
                {t('restaurantRevenueSources.restaurantName')}
              </label>
              <input
                type="text"
                id="restaurantName"
                className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:focus:ring-emerald-500/20 focus:border-emerald-500 transition"
                value={restaurantName}
                onChange={e => setRestaurantName(e.target.value)}
                placeholder={t('restaurantRevenueSources.restaurantNamePlaceholder')}
              />
            </div>

            {/* Relevant Sections */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100">
                  {t('restaurantRevenueSources.relevantSections')}
                </label>
                <button
                  type="button"
                  onClick={addSection}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-emerald-600 dark:text-emerald-400 border border-emerald-600 dark:border-emerald-400 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-950/30 transition"
                >
                  <Plus className="w-3 h-3" /> {t('restaurantRevenueSources.add')}
                </button>
              </div>
              <div className="space-y-2">
                {relevantSections.map((section, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      type="text"
                      className="flex-1 px-4 py-2.5 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:focus:ring-emerald-500/20 focus:border-emerald-500 transition"
                      value={section}
                      onChange={e => updateSection(index, e.target.value)}
                      placeholder={t('restaurantRevenueSources.sectionNamePlaceholder')}
                    />
                    <button
                      type="button"
                      onClick={() => removeSection(index)}
                      className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition"
                      aria-label={t('restaurantRevenueSources.removeSection')}
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {relevantSections.length === 0 && (
                  <p className="text-sm text-zinc-500 dark:text-zinc-400 italic">
                    {t('restaurantRevenueSources.noSectionsAdded')}
                  </p>
                )}
              </div>
            </div>

            {error && (
              <div className="p-3 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-100 dark:border-red-900/50 text-sm text-red-600 dark:text-red-400">
                {error}
              </div>
            )}
            
            <div className="flex gap-3 pt-2">
              <button
                onClick={onClose}
                disabled={saving}
                className="flex-1 px-6 py-3 rounded-2xl text-base font-semibold border border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition disabled:opacity-50"
              >
                {t('restaurantRevenueSources.cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 px-6 py-3 rounded-2xl text-base font-semibold bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (mode === 'create' ? t('restaurantRevenueSources.creating') : t('restaurantRevenueSources.saving')) : (mode === 'create' ? t('restaurantRevenueSources.create') : t('restaurantRevenueSources.saveChanges'))}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};